import { useEffect, useState } from 'react';
import { getMessageById, updateMessage, type Message, type Attachment, findRecipients, uploadAttachment } from "../../services/MessageService";
import { useNotification } from '../../components/Notifications';
import { Link } from 'react-router-dom';
import type { UserDTO } from '../../services/AuthService';
import { AttachmentsEdit } from './AttachmentsEdit';

export interface MessageFormProps {
  messageId:number
  onSuccess: () => void
}

export function MessageForm ({ messageId, onSuccess }: MessageFormProps) {
  const [message, setMessage] = useState({
    id:0,
    threadId:0,
    recipients: [] as UserDTO[],
    subject: '',
    content: '',
    draft: 'Y',
    attachments: [] as Attachment[],
  } as Message);

  const { addNotification } = useNotification();
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    if (messageId) {
      getMessageById(messageId).then((data:Message) => {
        setMessage(data);
      }).catch((err) => {
          setError("Error loading message " + err.message)
      });
    }
  }, [messageId]);

  const [recipientInput, setRecipientInput] = useState('');
  const [error, setError] = useState('');
  const [recipientError, setRecipientError] = useState('');
  const [subjectError, setSubjectError] = useState('');
  const [bodyError, setBodyError] = useState('');

  // Simple email validation regex
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  const IsRecipientAdded = (email) => {
    return message.recipients.filter(r => r.email == email).length > 0;
  }

  const validateForm = ():boolean => {
    if (message.recipients.length === 0) {
      setRecipientError('At least one valid recipient is required');
      return false;
    } else {
      setRecipientError('');
    }
    if (!message.subject) {
      setSubjectError('Subject is required');
      return false;
    } else {
      setSubjectError('');
    }
    if (!message.content) {
      setBodyError('Body is required');
      return false;
    } else {
      setBodyError('');
    }
    return true;
  }


  const handleAddRecipients = async (input) => {
    // Split input by commas or semicolons
    const newRecipients = input.split(/[,;]+/).map(r => r.trim()).filter(r => r && !IsRecipientAdded(r));
    if (newRecipients.length == 0) {
      setRecipientInput('');
      return;
    }
    const validRecipients = [];
    const invalidEmails = [];

    newRecipients.forEach(recipient => {
      if (isValidEmail(recipient)) {
        if (!message.recipients.includes(recipient)) {
          validRecipients.push(recipient);
        }
      } else {
        invalidEmails.push(recipient);
      }
    });

    if (invalidEmails.length > 0) {
      setRecipientError(`Invalid email(s): ${invalidEmails.join(', ')}`);
      return false;
    }

    if (validRecipients.length > 0) {
      let newValidRecipients = await getRecipients(validRecipients);
      setMessage({
        ...message,
        recipients: [...message.recipients, ...newValidRecipients]
      });
      setRecipientInput('');
      setRecipientError('');
      return true;
    }

    return false;
  };

  const getRecipients = async (list:string[]):Promise<UserDTO[]> => {
    try {
      return await findRecipients(list);
    } catch (err) {
      addNotification('Error finding recipients ' + err.message, 'error')
      return [];
    }
  }

  const handleRecipientInput = (e) => {
    setRecipientInput(e.target.value);
    setRecipientError('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      if (recipientInput.trim()) {
        handleAddRecipients(recipientInput);
      }
    }
  };

  const handleBlur = (e) => {
    e.preventDefault();
    if (recipientInput.trim()) {
      handleAddRecipients(recipientInput);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    handleAddRecipients(pastedText);
  };

  const handleRemoveRecipient = (recipient) => {
    setMessage({
      ...message,
      recipients: message.recipients.filter(r => r !== recipient)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (recipientInput.trim()) {
      handleAddRecipients(recipientInput);
    }
    if (!validateForm()) return;
    try {
      const msg = await updateMessage(message);
      for (const att of attachments) {
        try {
          const res = await uploadAttachment(msg.id, att);
          addNotification(`Attachment ${att.name} uploaded`, "success");
        } catch (err) {
          addNotification(`Failed to upload attachment ${att.name} : ` + err.message, "error");
        }
      }
      onSuccess();
    } catch (err) {
      addNotification("Error saving message " + err.message, 'error')
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6">{messageId ? 'Edit Message' : 'Compose Message'}</h2>
      
      {/* Recipients Input */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">To:</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {message.recipients.map((recipient, index) => (
            <span key={index} className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
              {recipient.email}
              <button
                type="button"
                onClick={() => handleRemoveRecipient(recipient)}
                className="ml-2 text-blue-600 hover:text-blue-800"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <input
          type="text"
          value={recipientInput}
          onChange={handleRecipientInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={handleBlur}
          placeholder="Enter or paste email(s), separated by commas or semicolons"
          className={`w-full px-3 py-2 border ${error ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
        />
        {recipientError && <p className="mt-1 text-sm text-red-500">{recipientError}</p>}
      </div>

      {/* Subject Input */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
        <input
          type="text"
          value={message.subject}
          onChange={(e) => setMessage({ ...message, subject: e.target.value })}
          placeholder="Enter subject"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {subjectError && <p className="mt-1 text-sm text-red-500">{subjectError}</p>}
      </div>

      {/* Body Input */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Body:</label>
        <textarea
          value={message.content}
          onChange={(e) => setMessage({ ...message, content: e.target.value })}
          placeholder="Enter message body"
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {bodyError && <p className="mt-1 text-sm text-red-500">{bodyError}</p>}
      </div>

      <AttachmentsEdit
        attachments={attachments}
        setAttachments={setAttachments}
      />

      <div className="flex gap-4">
        {error && <p className="mt-1 text-sm text-red-500 py-3">{error}</p>}
      </div>      
      {/* Submit Button */}
      <div className="flex gap-4 justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          className="flex-1 max-w-[140px] py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-500 hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
        >
          {messageId ? 'Update Message' : 'Send Message'}
        </button>
        <Link
          to="/messages"
          type="button"
          className="flex-1 max-w-[140px] py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-800 bg-gray-200 hover:bg-gray-300 text-center"
        >
          Cancel
        </Link>
      </div>
    </div>
  );
}


